import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { DateField } from '../../../../components/inputs/DateField';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { Select, type SelectOption } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { ppmpsApi } from '../ppmpsApi';
import type { PpmpDetail, PpmpLineItemDto } from '../ppmpTypes';
import { procurementCatalogsApi } from '../procurementCatalogsApi';
import type { ProcurementCatalogOption } from '../procurementCatalogTypes';

/**
 * Correct ONE procurement line, addressed by its own id.
 *
 * **Beside `<PpmpLineItemsModal>`, which ADDS a batch — two endpoints, two jobs.** An inline
 * grid holding one locked row would be a table pretending to be a form, and this is where the
 * fields the grid has no room for live: the full rich-text specification, the procurement
 * window, the conference flag and the remarks.
 *
 * **NO Autogenerate**, and that is `<ResetUserPasswordModal>`'s exemption rather than an
 * omission: every field arrives filled from the row the reader opened, so the contract — the
 * primary action enables on the first try — is met before the button would have anything to
 * do. It is also the one form where generating would be actively wrong, because this is a
 * CORRECTION and overwriting the values would destroy what the reader came to read.
 */
export function PpmpLineItemModal({
  open,
  ppmp,
  line,
  onClose,
  onSaved,
}: {
  open: boolean;
  ppmp: PpmpDetail;
  line: PpmpLineItemDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [description, setDescription] = useState(line.itemDescription);
  const [procurementTypeId, setProcurementTypeId] = useState<string | null>(
    line.procurementTypeId,
  );
  const [modeOfProcurementId, setModeOfProcurementId] = useState<string | null>(
    line.modeOfProcurementId,
  );
  const [quantity, setQuantity] = useState(String(line.quantity));
  const [unit, setUnit] = useState(line.unit);
  const [estimatedBudget, setEstimatedBudget] = useState(String(line.estimatedBudget));
  const [conference, setConference] = useState(line.preProcurementConference);
  const [startDate, setStartDate] = useState(line.procurementStartDate ?? '');
  const [endDate, setEndDate] = useState(line.procurementEndDate ?? '');
  const [deliveryDate, setDeliveryDate] = useState(line.expectedDeliveryDate ?? '');
  const [remarks, setRemarks] = useState(line.remarks ?? '');

  const [types, setTypes] = useState<ProcurementCatalogOption[]>([]);
  const [modes, setModes] = useState<ProcurementCatalogOption[]>([]);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    procurementCatalogsApi
      .options('types', controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setTypes(rows);
      })
      .catch(() => {
        // Fails soft — the save still refuses a retired or unknown row.
      });

    procurementCatalogsApi
      .options('modes', controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setModes(rows);
      })
      .catch(() => {});

    return () => controller.abort();
  }, []);

  /*
    A RETIRED catalog row is left alone where it is already recorded, so the option is injected
    when the line names one the live list no longer offers — otherwise the picker would show
    its placeholder and the form would read as though nothing were chosen.
  */
  function withCurrent(
    rows: ProcurementCatalogOption[],
    id: string | null,
    name: string,
  ): SelectOption[] {
    const options: SelectOption[] = rows.map((r) => ({ value: r.id, label: r.name }));

    if (id && !rows.some((r) => r.id === id)) {
      options.unshift({ value: id, label: name, sublabel: 'No longer offered' });
    }

    return options;
  }

  const quantityValue = Number(quantity);
  const budgetValue = Number(estimatedBudget);

  const canSave =
    plainTextOf(description).trim().length > 0 &&
    procurementTypeId !== null &&
    modeOfProcurementId !== null &&
    unit.trim().length > 0 &&
    Number.isFinite(quantityValue) &&
    quantityValue > 0 &&
    Number.isFinite(budgetValue) &&
    budgetValue >= 0 &&
    confirmed &&
    !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await ppmpsApi.updateLineItem(ppmp.id, line.id, {
        // The catalogue reference is KEPT: it says where the wording came from, and correcting
        // the wording does not change that.
        ppmpItemCatalogId: line.ppmpItemCatalogId,
        itemDescription: description,
        procurementTypeId,
        modeOfProcurementId,
        quantity: quantityValue,
        unit: unit.trim(),
        estimatedBudget: budgetValue,
        preProcurementConference: conference,
        procurementStartDate: startDate || null,
        procurementEndDate: endDate || null,
        expectedDeliveryDate: deliveryDate || null,
        remarks: remarks.trim() || null,
      });

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save the line.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'ppmp-line' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit procurement line"
      size="3xl"
      id="ppmp-line-modal"
      confirm={{
        id: 'ppmp-line-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="ppmp-line-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="ppmp-line-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <LazyRichTextField
          id="ppmp-line-item-description"
          label="Specification"
          required
          description="What is being bought, in as much detail as the procurement needs — a table of dimensions, a list of what is in the box."
          value={description}
          onChange={setDescription}
          disabled={saving}
          error={error?.fieldError('itemDescription')}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="ppmp-line-procurement-type-id"
            label="Procurement type"
            required
            description="What kind of procurement this is."
            value={procurementTypeId}
            onChange={setProcurementTypeId}
            options={withCurrent(types, line.procurementTypeId, line.procurementTypeName)}
            clearable={false}
            disabled={saving}
            error={error?.fieldError('procurementTypeId')}
          />

          <Select
            id="ppmp-line-mode-of-procurement-id"
            label="Mode of procurement"
            required
            description="How it is to be procured. RA 9184 caps each alternative mode, which is why this is a catalog rather than free text."
            value={modeOfProcurementId}
            onChange={setModeOfProcurementId}
            options={withCurrent(modes, line.modeOfProcurementId, line.modeOfProcurementName)}
            clearable={false}
            disabled={saving}
            error={error?.fieldError('modeOfProcurementId')}
          />

          <TextField
            id="ppmp-line-quantity"
            label="Quantity"
            required
            type="number"
            description="How many, in the unit beside it."
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            disabled={saving}
            error={error?.fieldError('quantity')}
          />

          <TextField
            id="ppmp-line-unit"
            label="Unit"
            required
            description="What the quantity is counted in — pc, box, lot."
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            disabled={saving}
            error={error?.fieldError('unit')}
          />

          <TextField
            id="ppmp-line-estimated-budget"
            label="Estimated budget"
            required
            type="number"
            description="What the line is expected to cost in TOTAL. Stored as entered rather than derived from the quantity — a planner routinely rounds to the figure the budget carries."
            value={estimatedBudget}
            onChange={(e) => setEstimatedBudget(e.target.value)}
            disabled={saving}
            error={error?.fieldError('estimatedBudget')}
          />
        </div>

        <label
          htmlFor="ppmp-line-pre-procurement-conference"
          className="flex items-start gap-2 text-sm text-slate-600"
        >
          <Checkbox
            id="ppmp-line-pre-procurement-conference"
            name="ppmp-line-pre-procurement-conference"
            checked={conference}
            onChange={() => setConference((v) => !v)}
            disabled={saving}
          />
          <span>A pre-procurement conference is held before this line is bid out.</span>
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="ppmp-line-procurement-start-date"
            label="Procurement from"
            description="When this line's own procurement is expected to begin."
            value={startDate}
            onChange={setStartDate}
            disabled={saving}
            error={error?.fieldError('procurementStartDate')}
          />

          <DateField
            id="ppmp-line-procurement-end-date"
            label="Procurement to"
            description="On or after the start date."
            value={endDate}
            onChange={setEndDate}
            disabled={saving}
            error={error?.fieldError('procurementEndDate')}
          />

          <DateField
            id="ppmp-line-expected-delivery-date"
            label="Expected delivery"
            description="When the goods or services are expected to arrive."
            value={deliveryDate}
            onChange={setDeliveryDate}
            disabled={saving}
            error={error?.fieldError('expectedDeliveryDate')}
          />
        </div>

        {/* A free note about the line as a whole — full width, outside the grid. */}
        <Textarea
          id="ppmp-line-remarks"
          label="Remarks"
          description="Anything a later reader needs to know about this line."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          disabled={saving}
          minRows={3}
          error={error?.fieldError('remarks')}
        />
      </div>
    </Modal>
  );
}

/**
 * The visible text of a rich-text document.
 *
 * An "empty" document out of the editor is `<p></p>`, not an empty string, so validity strips
 * the tags rather than measuring the markup — the same question the server's own validator
 * asks of the sanitized html.
 */
function plainTextOf(html: string): string {
  if (typeof document === 'undefined') return html;

  const host = document.createElement('div');
  host.innerHTML = html;

  return host.textContent ?? '';
}
